import React, { useMemo } from 'react';
import { ResourceSearchScreenParams } from '../../../utils/appNavigationNames';
import { SearchInputRecord, useApplySearchFilter, screenParamsToFilterValue } from './ResourceListSearchFilters';
import { APITypes, MedicalDataTypes } from '../../../primary-record-types';
import SearchResultsList from './SearchResultsList';
import { getIntl } from '../../../utils/intl';
import { StyleProp, ViewStyle } from 'react-native';
import { mainContentPadding } from '../../v2BaseComponents/MainContentContainer';
import Stack from '../../v2BaseComponents/Stack';
import Text from '../../v2BaseComponents/Text';
import { getSpacing } from '../../../utils/spacing';

const intl = getIntl('medical-data-list-static-search', {
    active: 'Active',
    inactive: 'Inactive',
    chronic: 'Chronic',
    'ruled-out': 'Ruled Out',
    routine: 'Routine',
    'walk-in': 'Walk-In',
    checkup: 'Checkup',
    followup: 'Followup',
    emergency: 'Emergency',
    other: 'Other',
    search: 'Search: "{search}"',
    status: 'Status: {status}',
    listFilter: 'List Filter',
    all: 'All',
});

export type StaticSearchListOwnProps = {
    screenSearchParams: ResourceSearchScreenParams;
    disableFlatListPadding?: boolean;
    renderFilteredResources: (resources: SearchInputRecord) => React.ReactElement;
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    resources: APITypes.medicalDataService.ListResourceResponseItem[];
    isLoadingResources: boolean;
    renderPreList?: () => React.ReactElement;
    style?: StyleProp<ViewStyle>;
};

export type StaticSearchListProps = StaticSearchListOwnProps;

const StaticSearchList: React.FC<StaticSearchListProps> = (props) => {
    const screenParamsAsFilterValue = useMemo(
        () => screenParamsToFilterValue(props.screenSearchParams),
        [props.screenSearchParams],
    );
    const filteredResources = useApplySearchFilter(props.resources || [], screenParamsAsFilterValue, props.category);

    const getFilterMessage = () => {
        const parts: string[] = [];
        if (screenParamsAsFilterValue.searchText) {
            parts.push(intl('search', { search: screenParamsAsFilterValue.searchText }));
        }

        if (props.category === 'allergy' && screenParamsAsFilterValue.allergyStatus) {
            parts.push(intl('status', { status: intl(screenParamsAsFilterValue.allergyStatus) }));
        } else if (props.category === 'condition' && screenParamsAsFilterValue.conditionStatus) {
            parts.push(intl('status', { status: intl(screenParamsAsFilterValue.conditionStatus) }));
        } else if (props.category === 'medication' && screenParamsAsFilterValue.medicationIsActive !== undefined) {
            parts.push(
                intl('status', { status: intl(screenParamsAsFilterValue.medicationIsActive ? 'active' : 'inactive') }),
            );
        } else if (props.category === 'visit' && screenParamsAsFilterValue.visitStatus) {
            parts.push(intl('status', { status: intl(screenParamsAsFilterValue.visitStatus) }));
        }

        return parts.join(', ');
    };

    const filterMessage = getFilterMessage();

    return (
        <>
            <Stack vertical fullWidth style={{ padding: mainContentPadding }}>
                {filterMessage ? (
                    <Stack vertical space={getSpacing(0.125)}>
                        <Text variant="body-1">{intl('listFilter')}</Text>
                        <Text variant="body-1">{filterMessage}</Text>
                    </Stack>
                ) : null}
                {props.renderPreList?.() || null}
            </Stack>
            <SearchResultsList
                isLoadingResources={props.isLoadingResources}
                disableFlatListPadding={props.disableFlatListPadding}
                filteredResources={filteredResources}
                renderFilteredResources={props.renderFilteredResources}
            />
        </>
    );
};

export default React.memo(StaticSearchList);
